<template>
    <v-container fluid>
        <v-row>
            <v-col cols="12" md="8">
                <v-alert type="info" outlined class="mb-4" v-if="dateStart && dateEnd">
                    <strong>Date Range:</strong> {{ dateStart }} to {{ dateEnd }}
                </v-alert>
            </v-col>
            <v-col cols="12" md="4" class="d-flex align-center justify-end">
                <v-btn
                    color="primary"
                    @click="downloadPdfReport"
                    :loading="downloadingPdf"
                    :disabled="downloadingPdf || !savedReportId"
                >
                    <v-icon left>mdi-file-pdf-box</v-icon>
                    Download PDF Report
                    <span v-if="!savedReportId" class="ml-2">(Loading...)</span>
                </v-btn>
            </v-col>
        </v-row>

        <!-- Loading State -->
        <v-row v-if="loading">
            <v-col cols="12" class="text-center">
                <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
                <div class="mt-4">Loading AI Summary Reports...</div>
            </v-col>
        </v-row>

        <!-- Error State -->
        <v-row v-if="error && !loading">
            <v-col cols="12">
                <v-alert type="error" outlined>
                    {{ error }}
                </v-alert>
            </v-col>
        </v-row>

        <!-- Content -->
        <v-row v-if="!loading && !error">
            <!-- Calls Summary Report -->
            <v-col cols="12" :md="reportType === 'both' ? 6 : 12" v-if="reportType === 'calls' || reportType === 'both'">
                <v-card outlined>
                    <v-card-title class="primary white--text">
                        <v-icon left color="white">mdi-phone</v-icon>
                        Calls Summary Report
                    </v-card-title>
                    <v-card-text class="pt-4">
                        <div v-if="callsSummary" class="report-text">
                            {{ callsSummary }}
                        </div>
                        <div v-else class="text-center grey--text">
                            No calls summary data available
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Web Inquiries Summary Report -->
            <v-col cols="12" :md="reportType === 'both' ? 6 : 12" v-if="reportType === 'webinq' || reportType === 'both'">
                <v-card outlined>
                    <v-card-title class="success white--text">
                        <v-icon left color="white">mdi-email</v-icon>
                        Web Inquiries Summary Report
                    </v-card-title>
                    <v-card-text class="pt-4">
                        <div v-if="webinqSummary" class="report-text">
                            {{ webinqSummary }}
                        </div>
                        <div v-else class="text-center grey--text">
                            No web inquiries summary data available
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import axios from "@/api/axios-auth";

export default {
    name: "SummaryAiReport",
    props: {
        weekData: {
            type: Object,
            default: null
        },
        dateStart: {
            type: String,
            default: null
        },
        dateEnd: {
            type: String,
            default: null
        },
        reportType: {
            type: String,
            default: 'both', // 'calls', 'webinq', or 'both'
            validator: (value) => ['calls', 'webinq', 'both'].includes(value)
        },
        reportId: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            loading: false,
            error: null,
            callsSummary: null,
            webinqSummary: null,
            downloadingPdf: false,
            savedReportId: null  // Store the report_id from the response
        };
    },
    mounted() {
        console.log('SummaryAiReport mounted with reportType:', this.reportType);
        console.log('Date range:', this.dateStart, 'to', this.dateEnd);
        console.log('Report ID (prop):', this.reportId);
        console.log('Saved Report ID (from API):', this.savedReportId);
        this.loadSummaryReports();
    },
    watch: {
        dateStart() {
            this.loadSummaryReports();
        },
        dateEnd() {
            this.loadSummaryReports();
        },
        reportType() {
            this.loadSummaryReports();
        }
    },
    methods: {
      loadSummaryReports: async function () {
        if (!this.dateStart || !this.dateEnd) {
          this.error = "Date range is required";
          return;
        }

        console.log('Loading summary reports with reportType:', this.reportType);

        this.loading = true;
        this.error = null;
        this.callsSummary = null;
        this.webinqSummary = null;

        try {
          const promises = [];

          // Conditionally load reports based on reportType
          if (this.reportType === 'calls' || this.reportType === 'both') {
            console.log('Loading CALLS report');
            promises.push(
                axios.get(`/reports/calls_range_summary_ai/${this.dateStart}/${this.dateEnd}`)
                    .then(response => {
                      return {type: 'calls', data: response.data};
                    })
            );
          }

          if (this.reportType === 'webinq' || this.reportType === 'both') {
            console.log('Loading WEB INQUIRIES report');
            promises.push(
                axios.get(`/reports/webinq_range_summary_ai/${this.dateStart}/${this.dateEnd}`)
                    .then(response => {
                      return {type: 'webinq', data: response.data};
                    })
            );
          }

          // Wait for all requested reports
          const results = await Promise.all(promises);

          // Process results
          results.forEach(result => {
            if (result.type === 'calls' && result.data) {
              this.callsSummary = this.extractSummaryText(result.data);
              // Save the report_id from the response
              if (result.data.report_id) {
                this.savedReportId = result.data.report_id;
                console.log('✓ Saved report_id from CALLS response:', this.savedReportId);
              }
            } else if (result.type === 'webinq' && result.data) {
              this.webinqSummary = this.extractSummaryText(result.data);
              // Save the report_id from the response (if not already saved from calls)
              if (result.data.report_id && !this.savedReportId) {
                this.savedReportId = result.data.report_id;
                console.log('✓ Saved report_id from WEBINQ response:', this.savedReportId);
              }
            }
          });

          this.loading = false;
        } catch (err) {
          console.error('Error loading summary reports:', err);
          this.error = err.response?.data?.message || err.message || 'Failed to load summary reports';
          this.loading = false;
        }
      },

        extractSummaryText(responseData) {
            // Handle different response formats
            if (typeof responseData === 'string') {
                return responseData;
            }

            // Check for 'report' key first (primary expected format)
            if (responseData.report) {
                return responseData.report;
            }

            if (responseData.summary) {
                return responseData.summary;
            }

            if (responseData.data) {
                return typeof responseData.data === 'string' ? responseData.data : JSON.stringify(responseData.data, null, 2);
            }

            // If it's an object, convert to formatted JSON
            return JSON.stringify(responseData, null, 2);
        },

        async downloadPdfReport() {
            // Use savedReportId from API response, fallback to prop if available
            const reportIdToUse = this.savedReportId || this.reportId;

            if (!reportIdToUse) {
                console.error('No report ID available for PDF download');
                this.error = 'Report ID not available. Please wait for the report to load.';
                return;
            }

            this.downloadingPdf = true;

            try {
                // Determine report name based on reportType
                let reportName = '';
                if (this.reportType === 'calls') {
                    reportName = 'Calls';
                } else if (this.reportType === 'webinq') {
                    reportName = 'Web Inquiries';
                } else if (this.reportType === 'both') {
                    reportName = 'Calls and Web Inquiries';
                }

                // Make the API request with responseType 'blob' to handle binary data
                // Pass report_name as query parameter
                const response = await axios.get(`/reports/call_report_pdf/${reportIdToUse}`, {
                    params: {
                        report_name: reportName
                    },
                    responseType: 'blob'
                });

                // Create a blob from the response
                const blob = new Blob([response.data], { type: 'application/pdf' });

                // Create a temporary URL for the blob
                const url = window.URL.createObjectURL(blob);

                // Create a temporary anchor element and trigger download
                const link = document.createElement('a');
                link.href = url;
                // Use report name in filename
                const sanitizedReportName = reportName.replace(/\s+/g, '_');
                link.download = `${sanitizedReportName}_report_${reportIdToUse}_${this.dateStart}_${this.dateEnd}.pdf`;
                document.body.appendChild(link);
                link.click();

                // Clean up
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                console.log('PDF downloaded successfully');
            } catch (error) {
                console.error('Error downloading PDF:', error);
                this.error = error.response?.data?.message || error.message || 'Failed to download PDF report';
            } finally {
                this.downloadingPdf = false;
            }
        }
    }
};
</script>

<style scoped>
.report-text {
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: 'Roboto', sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #333;
}
</style>